import {
  CHANNEL_PROOFREAD_GRAMMAR_ANALYZE_EVENT,
  CHANNEL_PROOFREAD_PARAPHRASE_ANALYZE_EVENT,
} from '@chegg-wt/core/constants/pusher'
import { oneGraph } from '@chegg-wt/core/api/oneGraph'
import { CheckTypeInput } from '@chegg-wt/core/globalTypesOneGraph'
import { store } from '@chegg-wt/core/state'
import {
  PusherGrammarFeedback,
  PusherParaphraseFeedback,
  SentenceOffset,
  ToastType,
  WtPusherEvent,
} from '@chegg-wt/core/state/paraphraser/types'
import { FeedbackType } from '@chegg-wt/core/types/paraphraser'
import {
  FULL_CHECK_TIMEOUT,
  SELECTION_CHECK_TIMEOUT,
  SENTENCE_FETCH_RETRY_COUNT,
} from '@chegg-wt/core/constants/paraphraser'
import {
  debounceOnSavePaper,
  handlePusherGrammarEvent,
  handlePusherParaphraseEvent,
} from '@chegg-wt/core/state/paraphraser/utils'
import { Dispatch } from 'redux'
import {
  setFetchNewSentencesLoading,
  notifyToast,
} from '@chegg-wt/core/state/paraphraser/actions'
import pusherManager from '../pusherManager'
import history from '../history/history'
import lruCache from '../lruCache'

interface WaitAndMakeSentenceCheckCallParams {
  sentences: { id: string; originalText: string }[]
  paperId: string
  traceId: string
  initiatedAt: string
  checkType: CheckTypeInput
  dispatch: Dispatch
}

class NewFeedbacksOnEditHandler {
  private timeouts = {}

  private sentencesToFetch = new Set<string>()

  private retriesLeft = {}

  private traceId: string

  private paperId: string

  private getSentenceAndTypeUnionId = (sentenceId: string, checkType: CheckTypeInput) => {
    return `${sentenceId}_${checkType}`
  }

  private checkLoading = (dispatch: Dispatch) => {
    if (this.sentencesToFetch.size === 0) {
      dispatch(setFetchNewSentencesLoading(false))
    }
  }

  private handleEventData = (eventData: WtPusherEvent, dispatch: Dispatch) => {
    const currentTime = new Date().getTime()
    const initiatedAt = new Date(eventData.initiatedAt).getTime()
    if (!eventData.error && eventData.feedback) {
      // Add response to cache
      lruCache.addResponseToCacheUsingSentenceId(
        eventData.sentence.id,
        eventData.initiatedAt,
        eventData.checkType,
        eventData.feedback
      )
    }
    // Here we are checking fullCheckoutTimeInMS because there can be 3 retry and it is combination of all
    if (currentTime - initiatedAt <= FULL_CHECK_TIMEOUT) {
      const { getState } = store
      const state = getState()
      const sentence = state.paraphraser.sentenceOffsets.find(
        (sent) => sent.id === eventData.sentence.id
      )
      const sentenceAndTypeId = this.getSentenceAndTypeUnionId(
        sentence?.id || '',
        eventData.checkType
      )

      // 1. Event is for valid sentence
      // 2. Event is for latest sentence
      // 3. Event is for required sentence
      if (
        sentence &&
        sentence.lastUpdated <= initiatedAt &&
        this.sentencesToFetch.has(sentenceAndTypeId)
      ) {
        clearTimeout(this.timeouts[sentenceAndTypeId])
        delete this.timeouts[sentenceAndTypeId]
        this.sentencesToFetch.delete(sentenceAndTypeId)

        // check the error
        if (eventData.error) {
          if (this.retriesLeft[sentenceAndTypeId] > 0) {
            this.handleRetry(
              [
                {
                  id: sentence.id,
                  originalText: sentence.originalText,
                },
              ],
              eventData.initiatedAt,
              eventData.checkType,
              dispatch
            )
            return
          }
          dispatch(
            notifyToast(
              eventData.checkType === CheckTypeInput.GRAMMAR
                ? ToastType.GRAMMAR_ERROR
                : ToastType.CLARITY_ERROR
            )
          )
          this.checkLoading(dispatch)
          delete this.retriesLeft[sentenceAndTypeId]
          lruCache.removeTheSentenceText(
            sentence.id,
            eventData.initiatedAt,
            eventData.checkType
          )
          return
        }

        // handle the data
        if (eventData.checkType === CheckTypeInput.GRAMMAR) {
          handlePusherGrammarEvent(eventData as WtPusherEvent<PusherGrammarFeedback>)
        } else {
          handlePusherParaphraseEvent(
            eventData as WtPusherEvent<PusherParaphraseFeedback>
          )
        }
        debounceOnSavePaper(dispatch)
        delete this.retriesLeft[sentenceAndTypeId]
        this.checkLoading(dispatch)
      }
    }
  }

  private handleRetry(
    sentences: { id: string; originalText: string }[],
    initiatedAt: string,
    checkType: CheckTypeInput,
    dispatch: Dispatch
  ) {
    this.makeSentenceCheckCall(
      sentences,
      this.paperId,
      this.traceId,
      initiatedAt,
      checkType,
      dispatch
    )
    this.handleNewAdditionInFetchingFeedback(
      sentences,
      checkType,
      dispatch,
      initiatedAt,
      true
    )
  }

  public handleNewAdditionInFetchingFeedback(
    sentences: { id: string }[],
    checkType: CheckTypeInput,
    dispatch: Dispatch,
    initatedAt: string,
    isRetry?: boolean
  ) {
    sentences.forEach((sentence) => {
      const sentenceAndTypeId = this.getSentenceAndTypeUnionId(sentence.id, checkType)
      // Clear old timeout for this sentence
      if (this.timeouts[sentenceAndTypeId]) {
        clearTimeout(this.timeouts[sentenceAndTypeId])
      }

      // Add a new timeout
      this.timeouts[sentenceAndTypeId] = setTimeout(() => {
        // Delete all related data of this sentence from class
        this.sentencesToFetch.delete(sentenceAndTypeId)
        delete this.timeouts[sentenceAndTypeId]
        delete this.retriesLeft[sentenceAndTypeId]
        dispatch(
          notifyToast(
            checkType === CheckTypeInput.GRAMMAR
              ? ToastType.GRAMMAR_ERROR
              : ToastType.CLARITY_ERROR
          )
        )
        this.checkLoading(dispatch)
        lruCache.removeTheSentenceText(sentence.id, initatedAt, checkType)
      }, SELECTION_CHECK_TIMEOUT)
      this.retriesLeft[sentenceAndTypeId] = isRetry
        ? this.retriesLeft[sentenceAndTypeId] - 1
        : SENTENCE_FETCH_RETRY_COUNT
      this.sentencesToFetch.add(sentenceAndTypeId)
    })
  }

  private handleErrorInSentcenCheckCall = (
    sentences: { id: string }[],
    checkType: CheckTypeInput,
    dispatch: Dispatch
  ) => {
    sentences.forEach((sentence) => {
      const sentenceAndTypeId = this.getSentenceAndTypeUnionId(sentence.id, checkType)
      if (this.timeouts[sentenceAndTypeId]) {
        clearTimeout(this.timeouts[sentenceAndTypeId])
      }
      this.sentencesToFetch.delete(sentenceAndTypeId)
      delete this.timeouts[sentenceAndTypeId]
      delete this.retriesLeft[sentenceAndTypeId]
      dispatch(
        notifyToast(
          checkType === CheckTypeInput.GRAMMAR
            ? ToastType.GRAMMAR_ERROR
            : ToastType.CLARITY_ERROR
        )
      )
      this.checkLoading(dispatch)
    })
  }

  public makeSentenceCheckCall = (
    sentences: { id: string; originalText: string }[],
    paperId: string,
    traceId: string,
    initiatedAt: string,
    checkType: CheckTypeInput,
    dispatch: Dispatch
  ) => {
    sentences.forEach((sentence) => {
      lruCache.storeTheSentenceText(
        sentence.id,
        sentence.originalText,
        initiatedAt,
        checkType
      )
    })
    oneGraph
      .sentenceCheck({
        request: {
          sentences,
          paperId,
          traceId,
          initiatedAt,
          checkType,
        },
      })
      .catch(() => {
        this.handleErrorInSentcenCheckCall(sentences, checkType, dispatch)
      })
  }

  // Return true if we add some sentences in graphql to indicate start of loading
  addWaitAndMakeSentenceCheckCall = ({
    sentences,
    paperId,
    traceId,
    initiatedAt,
    checkType,
    dispatch,
  }: WaitAndMakeSentenceCheckCallParams): boolean => {
    this.paperId = paperId
    this.traceId = traceId
    // Store the sentence text to be used when we get response from pusher
    const filteredSentences = sentences.filter((sentence) => {
      const cachedEvent = lruCache.getEventFromCache(sentence, initiatedAt, checkType)
      if (!cachedEvent) {
        return true // send sentence call
      }
      // cached Event is present, handle the data
      const sentenceAndTypeId = this.getSentenceAndTypeUnionId(sentence.id, checkType)
      this.sentencesToFetch.delete(sentenceAndTypeId)
      this.checkLoading(dispatch)
      if (checkType === CheckTypeInput.GRAMMAR) {
        handlePusherGrammarEvent(cachedEvent as WtPusherEvent<PusherGrammarFeedback>)
      } else {
        handlePusherParaphraseEvent(
          cachedEvent as WtPusherEvent<PusherParaphraseFeedback>
        )
      }
      return false // do not send sentence call
    })
    if (filteredSentences.length === 0) return false

    this.handleNewAdditionInFetchingFeedback(
      filteredSentences,
      checkType,
      dispatch,
      initiatedAt
    )
    this.makeSentenceCheckCall(
      filteredSentences,
      paperId,
      traceId,
      initiatedAt,
      checkType,
      dispatch
    )
    return true
  }

  clearInternalState = (dispatch: Dispatch) => {
    // We trigger it on paper review so that if there is any sentence check already in review, we remove them as we are checking full paper again
    this.timeouts = {}
    this.sentencesToFetch.clear()
    this.retriesLeft = {}
    this.checkLoading(dispatch)
  }

  subscribeChannels = async (dispatch: Dispatch, nonManualUpdateType: FeedbackType) => {
    try {
      await pusherManager.subscribe(
        CHANNEL_PROOFREAD_PARAPHRASE_ANALYZE_EVENT,
        (eventData) => this.handleEventData(eventData, dispatch)
      )
      await pusherManager.subscribe(
        CHANNEL_PROOFREAD_GRAMMAR_ANALYZE_EVENT,
        (eventData) => this.handleEventData(eventData, dispatch)
      )
    } catch {
      dispatch(
        notifyToast(
          nonManualUpdateType === FeedbackType.SYNONYM
            ? ToastType.GRAMMAR_ERROR
            : ToastType.CLARITY_ERROR
        )
      )
      throw Error()
    }
  }

  fetchNewSentences = async (
    paperId: string,
    sentencesEdited: SentenceOffset[],
    initiatedAt: string,
    nonManualUpdateType: FeedbackType,
    dispatch: Dispatch
  ) => {
    try {
      await this.subscribeChannels(dispatch, nonManualUpdateType)
    } catch {
      // If we got any error in pusher connection we will not process sentences
      return
    }
    const traceId = await pusherManager.getTraceId()

    this.traceId = traceId
    this.paperId = paperId

    const sentences: { id: string; originalText: string }[] = []
    sentencesEdited.forEach((sentence) => {
      if (sentence.originalText)
        sentences.push({
          id: sentence.id,
          originalText: sentence.originalText,
        })
    })
    if (sentences.length === 0) return
    let setLoading = false
    if (
      nonManualUpdateType !== FeedbackType.GRAMMAR &&
      nonManualUpdateType !== FeedbackType.PARAPHRASE
    ) {
      const isSentencesAddedToGraphlCall = this.addWaitAndMakeSentenceCheckCall({
        sentences,
        paperId,
        traceId,
        initiatedAt,
        checkType: CheckTypeInput.GRAMMAR,
        dispatch,
      })
      if (isSentencesAddedToGraphlCall) setLoading = true

      // Add this fetching information in history
      const sentenceIds = sentences.map((sentence) => sentence.id)
      history.addGrammarFeedbackSentences(sentenceIds)
    }

    if (nonManualUpdateType !== FeedbackType.PARAPHRASE) {
      const isSentencesAddedToGraphlCall = this.addWaitAndMakeSentenceCheckCall({
        sentences,
        paperId,
        traceId,
        initiatedAt,
        checkType: CheckTypeInput.PARAPHRASE,
        dispatch,
      })
      if (isSentencesAddedToGraphlCall) setLoading = true

      // Add this fetching information in history
      const sentenceIds = sentences.map((sentence) => sentence.id)
      history.addParaphraseFeedbackSentences(sentenceIds)
    }
    if (setLoading) {
      dispatch(setFetchNewSentencesLoading(true))
    }
  }
}

export const newFeedbacksOnEditHandler = new NewFeedbacksOnEditHandler()
